<template>
  <div>
    <div class="paa-comm-grid">
      <div>
        <pa-admin-toolbar
          :per-page="perPage"
          :search.sync="searchInput"
          :from.sync="fromInput"
          :to.sync="toInput"
          :busy="loading"
          @per-page="setPerPage"
          @search="applySearch"
        />
        <div class="paa-filters">
          <pa-admin-filter
            v-if="hasFullView"
            label="SEO Manager"
            :value="filters.campaignManager"
            :options="managerOptions"
            @input="setFilter('campaignManager', $event)"
          />
          <pa-admin-filter
            v-if="hasFullView"
            label="Commission Owner"
            :value="filters.commissionOwner"
            :options="managerOptions"
            @input="setFilter('commissionOwner', $event)"
          />
          <pa-admin-filter label="Order Type" :value="filters.orderType" :options="orderTypeOptions" @input="setFilter('orderType', $event)" />
        </div>
      </div>

      <div class="paa-card paa-summary">
        <div class="paa-summary__line">Paid orders: <b>{{ (aggregations.count_active || 0) + (aggregations.count_passive || 0) }}</b></div>
        <div class="paa-summary__line">Total commissions amount: <b>{{ money(aggregations.commissions_total) || '$0.00' }}</b></div>
        <div class="paa-summary__owners">
          <span v-for="owner in ownerTotals" :key="owner.key">{{ owner.name }} <b>{{ money(owner.commissions_total) || '$0.00' }}</b></span>
        </div>
        <button type="button" class="paa-btn paa-btn--primary paa-btn--wide" :disabled="!allRecords.length" @click="exportOrders">Export Orders</button>
        <template v-if="hasFullView">
          <button type="button" class="paa-btn paa-btn--secondary paa-btn--wide" @click="exportAggregations">Export Analytics (for active filters)</button>
          <button type="button" class="paa-btn paa-btn--secondary paa-btn--wide" @click="exportResults(null, 'All time')">Export Analytics (All time results)</button>
          <div class="paa-menu-wrap">
            <button
              type="button"
              class="paa-btn paa-btn--secondary paa-btn--wide"
              :aria-expanded="showMonths ? 'true' : 'false'"
              @click.stop="showMonths = !showMonths"
            >Export Analytics (monthly results)</button>
            <div v-if="showMonths" class="paa-menu">
              <button
                v-for="option in monthOptions"
                :key="option.key"
                type="button"
                class="paa-menu__item"
                @click="exportResults(option.date, option.key)"
              >{{ option.label }}</button>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div class="paa-rates-row">
      <a href="#" @click.prevent="showRates = !showRates">{{ showRates ? 'Hide' : ratesVerb }} fixed commission rates</a>
    </div>
    <commission-rates-panel v-if="showRates" @saved="loadData" />

    <div v-if="loadError" class="paa-error">{{ loadError }}</div>

    <div class="paa-card paa-tblwrap">
      <table class="paa-tbl">
        <thead>
          <tr>
            <th>User ID</th>
            <th>Email</th>
            <th>Order type</th>
            <th>Item</th>
            <th class="paa-r">Invoice ID</th>
            <th class="paa-sorted">&#8595; Paid on</th>
            <th class="paa-r">Amount ($)</th>
            <th>SEO Manager</th>
            <th>Commission Owner</th>
            <th class="paa-r">Commission amount</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in pageRecords" :key="row.key">
            <td>{{ row.userId }}</td>
            <td>
              <a :href="userAccountsHref(row.userEmail)" target="_blank" rel="noopener">{{ row.userEmail }}</a>
              <span class="paa-sub">{{ row.siteName }}</span>
            </td>
            <td>{{ row.orderType }}</td>
            <td>{{ row.item }}</td>
            <td class="paa-r">{{ row.id }}</td>
            <td>{{ row.paidOn || '' }}</td>
            <td class="paa-r">{{ money(row.amount) }}</td>
            <td>{{ row.campaignManagerName }}</td>
            <td>
              <span v-if="ownerEditing === row.key" class="paa-in-edit">
                <select v-model="ownerValue" :disabled="saving" aria-label="Commission owner">
                  <option v-for="manager in managers" :key="manager.key" :value="manager.key">{{ manager.name }}</option>
                </select>
                <button type="button" class="paa-btn paa-btn--primary paa-btn--sm" :disabled="saving || !ownerValue" @click="saveOwner(row)">Save</button>
                <button type="button" class="paa-btn paa-btn--ghost paa-btn--sm" :disabled="saving" @click="ownerEditing = null">Cancel</button>
              </span>
              <template v-else>
                {{ row.commissionOwnerName }}
                <a v-if="canEdit" href="#" class="paa-edit" title="Change owner" @click.prevent="editOwner(row)">&#9998;</a>
              </template>
            </td>
            <td class="paa-r paa-num">
              <span v-if="amountEditing === row.key" class="paa-in-edit">
                $
                <input
                  ref="amountInput"
                  v-model="amountValue"
                  type="number"
                  step="0.5"
                  min="0"
                  :disabled="saving"
                  aria-label="Commission amount"
                  @keydown.enter.prevent="saveAmount(row)"
                  @keydown.esc.prevent="amountEditing = null"
                />
                <button type="button" class="paa-btn paa-btn--primary paa-btn--sm" :disabled="saving" @click="saveAmount(row)">Save</button>
                <button type="button" class="paa-btn paa-btn--ghost paa-btn--sm" :disabled="saving" @click="amountEditing = null">Cancel</button>
                <button v-if="row.isOverridden" type="button" class="paa-btn paa-btn--ghost paa-btn--sm" :disabled="saving" @click="resetAmount(row)">Reset</button>
              </span>
              <template v-else>
                <span :title="row.rateLabel || null">{{ money(row.commissionAmount) || '$0.00' }}</span>
                <span v-if="row.isOverridden" class="paa-ovtag" :title="`Manually set (fixed rate: ${money(row.commissionFrozen)})`">edited</span>
                <a v-if="canEdit" href="#" class="paa-edit" title="Edit commission" @click.prevent="editAmount(row)">&#9998;</a>
              </template>
            </td>
          </tr>
          <tr v-if="!pageRecords.length">
            <td class="paa-empty" colspan="10">{{ loading ? 'Loading…' : 'No paid order matches this search.' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <pa-admin-pager :page="page" :page-count="pageCount" @change="setPage" />
  </div>
</template>

<script>
import moment from 'moment'
import { showPopAuthorityToast } from '@/utils/popAuthority'
import CommissionRatesPanel from './CommissionRatesPanel.vue'
import PaAdminFilter from './PaAdminFilter.vue'
import PaAdminPager from './PaAdminPager.vue'
import PaAdminToolbar from './PaAdminToolbar.vue'
import {
  PA_ADMIN_ORDER_TYPE_OPTIONS,
  paAdminCsvName,
  paAdminDownloadCsv,
  paAdminMoney,
} from './paAdminConstants'

const DATE_FORMAT = 'YYYY-MM-DD'
// POP Authority commissions are fixed amounts, so there is no active / passive sale breakdown
const ANALYTICS_HEADER = [
  'Orders Count', 'Total Commissions ($)', 'Total Revenue ($)', 'Clients Count',
  'Clients Older Than 1 Month Count', 'Revenue/Client Avg ($)',
]

function analyticsCells (aggregations) {
  const a = aggregations || {}
  return [
    (a.count_active || 0) + (a.count_passive || 0), a.commissions_total, a.purchase_amount_total,
    a.count_clients, a.count_clients_older_than_1_month, a.purchase_amount_total_avg_per_client,
  ]
}

/**
 * Commissions Tracker: the paid POP Authority orders with their fixed commission (frozen with the
 * rates in force when the order was paid). With edit access a commission can be set by hand and
 * reset, and the commission owner changed. Filters and exports follow the White Glove tracker:
 * without full view a manager only sees their own commissions and the orders export.
 */
export default {
  name: 'AdminPopAuthorityCommissionsTracker',
  components: { CommissionRatesPanel, PaAdminFilter, PaAdminPager, PaAdminToolbar },
  data () {
    const from = moment().subtract(31, 'days').format(DATE_FORMAT)
    const to = moment().format(DATE_FORMAT)
    return {
      loading: false,
      loadError: '',
      allRecords: [],
      aggregations: {},
      managers: [],
      canEdit: false,
      page: 1,
      perPage: 15,
      searchInput: '',
      search: '',
      fromInput: from,
      toInput: to,
      from,
      to,
      filters: { campaignManager: 'all', commissionOwner: 'all', orderType: 'all' },
      orderTypeOptions: PA_ADMIN_ORDER_TYPE_OPTIONS,
      showRates: false,
      showMonths: false,
      saving: false,
      amountEditing: null,
      amountValue: '',
      ownerEditing: null,
      ownerValue: '',
      requestId: 0,
    }
  },
  computed: {
    currentUser () {
      return this.$store.state.user || {}
    },
    hasFullView () {
      return Boolean(this.currentUser.has_full_view_access_to_pop_authority_commissions)
    },
    ratesVerb () {
      return this.canEdit ? 'Edit' : 'View'
    },
    managerOptions () {
      return [{ key: 'all', name: 'All' }].concat(this.managers || [])
    },
    ownerTotals () {
      return this.aggregations.by_owner || []
    },
    pageCount () {
      return Math.max(1, Math.ceil(this.allRecords.length / this.perPage))
    },
    pageRecords () {
      return this.allRecords.slice((this.page - 1) * this.perPage, this.page * this.perPage)
    },
    monthOptions () {
      const options = []
      for (let index = 0; index <= 11; index++) {
        const date = moment().startOf('month').subtract(index, 'months')
        options.push({ date: date.toDate(), key: date.format('YYYY-MM'), label: date.format('MMMM YYYY') })
      }
      return options
    },
  },
  created () {
    this.loadData()
  },
  mounted () {
    document.addEventListener('click', this.closeMonths)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.closeMonths)
  },
  methods: {
    money: paAdminMoney,
    closeMonths () {
      this.showMonths = false
    },
    notifyError (text) {
      this.$notify({ group: 'info', type: 'error', text })
    },
    errorText (error, fallback) {
      return (error && error.response && error.response.data && error.response.data.msg) || fallback
    },
    userAccountsHref (email) {
      return this.$router.resolve({
        path: `/admin/${this.currentUser.id}`,
        query: { activeTab: 'userAccounts', search: email },
      }).href
    },
    buildParams (values) {
      const params = new URLSearchParams()
      Object.keys(values).forEach((key) => {
        const value = values[key]
        if (value !== null && value !== undefined && value !== '') params.set(key, value)
      })
      return params.toString()
    },
    listParams () {
      const values = { filterText: this.search, ...this.filters }
      if (this.from) values.startDate = moment(this.from, DATE_FORMAT).startOf('day').valueOf()
      if (this.to) values.endDate = moment(this.to, DATE_FORMAT).endOf('day').valueOf()
      return this.buildParams(values)
    },
    fetch (params) {
      return this.$store.dispatch('getPopAuthorityCommissionsForAdmin', { params }).then((response) => {
        const data = response && response.data
        if (!data || data.status !== 'SUCCESS') throw new Error((data && data.msg) || 'failed')
        return data
      })
    },
    loadData () {
      const id = ++this.requestId
      this.loading = true
      this.loadError = ''
      return this.fetch(this.listParams())
        .then((data) => {
          if (id !== this.requestId) return
          this.allRecords = data.records || []
          this.aggregations = data.aggregations || {}
          this.managers = data.managers || []
          this.canEdit = Boolean(data.canEdit)
          if (this.page > this.pageCount) this.page = this.pageCount
        })
        .catch((error) => {
          if (id !== this.requestId) return
          this.allRecords = []
          this.aggregations = {}
          this.loadError = this.errorText(error, 'We could not load the commissions. Try again in a moment.')
        })
        .finally(() => {
          if (id === this.requestId) this.loading = false
        })
    },
    applySearch () {
      this.search = (this.searchInput || '').trim()
      this.from = this.fromInput
      this.to = this.toInput
      this.page = 1
      return this.loadData()
    },
    setPerPage (value) {
      this.perPage = parseInt(value, 10) || 15
      this.page = 1
    },
    setPage (value) {
      this.page = Math.min(Math.max(parseInt(value, 10) || 1, 1), this.pageCount)
    },
    setFilter (key, value) {
      this.$set(this.filters, key, value)
      this.page = 1
      return this.loadData()
    },

    // ---- edits
    editAmount (row) {
      this.ownerEditing = null
      this.amountEditing = row.key
      this.amountValue = row.commissionAmount
      this.$nextTick(() => {
        const input = [].concat(this.$refs.amountInput || [])[0]
        if (input) {
          input.focus()
          input.select()
        }
      })
    },
    editOwner (row) {
      this.amountEditing = null
      this.ownerEditing = row.key
      this.ownerValue = row.commissionOwner || ''
    },
    update (row, body, successText) {
      if (this.saving) return Promise.resolve(false)
      this.saving = true
      return this.$store
        .dispatch('updatePopAuthorityCommissionForAdmin', { orderType: row.orderType, orderId: row.id, body })
        .then((response) => {
          const data = response && response.data
          if (!data || data.status !== 'SUCCESS') {
            this.notifyError((data && data.msg) || 'The commission could not be updated.')
            return false
          }
          this.amountEditing = null
          this.ownerEditing = null
          showPopAuthorityToast(this, successText)
          // totals and per-owner amounts come from the server
          return this.loadData().then(() => true)
        })
        .catch((error) => {
          this.notifyError(this.errorText(error, 'The commission could not be updated.'))
          return false
        })
        .finally(() => {
          this.saving = false
        })
    },
    saveAmount (row) {
      const value = parseFloat(this.amountValue)
      if (!Number.isFinite(value) || value < 0) {
        this.notifyError('Enter a commission amount of 0 or more.')
        return null
      }
      return this.update(row, { commissionAmount: value }, `Commission set to ${this.money(value)}`)
    },
    resetAmount (row) {
      return this.update(row, { resetCommission: true }, `Commission back to ${this.money(row.commissionFrozen)}`)
    },
    saveOwner (row) {
      if (!this.ownerValue) return null
      const manager = (this.managers || []).find((item) => item.key === this.ownerValue)
      return this.update(
        row, { commissionOwner: this.ownerValue },
        `Commission owner changed to ${manager ? manager.name : this.ownerValue}`)
    },

    // ---- exports (same files as the White Glove tracker)
    exportOrders () {
      const header = [
        'User ID', 'Email', 'Project', 'Order Type', 'Item', 'Invoice ID', 'Paid on', 'Amount ($)', 'SEO Manager',
        'Commission Owner', 'Commission Amount ($)', 'Fixed rate ($)', 'Manually edited',
      ]
      const rows = this.allRecords.map((row) => [
        row.userId, row.userEmail, row.siteName, row.orderType, row.item, row.id, row.paidOn || '', row.amount,
        row.campaignManagerName, row.commissionOwnerName, row.commissionAmount,
        row.commissionFrozen, row.isOverridden ? 'yes' : 'no',
      ])
      paAdminDownloadCsv(paAdminCsvName('commissions'), [header].concat(rows))
    },
    exportAggregations () {
      paAdminDownloadCsv(
        paAdminCsvName('commissions-analytics'), [ANALYTICS_HEADER, analyticsCells(this.aggregations)])
    },
    exportResults (monthDate, period) {
      this.showMonths = false
      this.$store.commit('showLoading')
      const rows = []
      return Promise.all((this.managers || []).map((manager) => {
        const values = { commissionOwner: manager.key }
        if (monthDate) {
          values.startDate = moment(monthDate).startOf('month').valueOf()
          values.endDate = moment(monthDate).endOf('month').valueOf()
        }
        return this.fetch(this.buildParams(values)).then((data) => {
          rows.push([period, manager.name].concat(analyticsCells(data.aggregations)))
        })
      }))
        .then(() => {
          rows.sort((a, b) => String(a[1]).localeCompare(String(b[1])))
          paAdminDownloadCsv(
            `pop-authority-results_${String(period).replace(/\s+/g, '-')}.csv`,
            [['Period', 'Commission Owner'].concat(ANALYTICS_HEADER)].concat(rows))
        })
        .catch((error) => {
          this.notifyError(this.errorText(error, 'The export failed. Try again in a moment.'))
        })
        .finally(() => {
          this.$store.commit('hideLoading')
        })
    },
  },
}
</script>
